// Mapbox Vanilla GL Component Renderer for GrapesJS
import React from 'react';
import { createRoot } from 'react-dom/client';

// Lazy load heavy mapbox components
const LazyMapboxWithPins = React.lazy(() => import('../components/real-estate-blocks/maps/MapboxWithPins').then(module => ({ default: module.default })));
const LazyMapboxSearchWithFilters = React.lazy(() => import('../components/real-estate-blocks/maps/MapboxSearchWithFilters').then(module => ({ default: module.default })));
const LazyMapboxWithDraw = React.lazy(() => import('../components/real-estate-blocks/maps/MapboxWithDraw').then(module => ({ default: module.default })));

export function registerMapboxRenderer(editor) {
  console.log('🗺️ Registering Mapbox Vanilla GL renderer...');

  // Remove Mapbox logo from editor canvas (attribution text is kept for ToS compliance)
  if (!document.getElementById('gjs-mapbox-logo-hide')) {
    const s = document.createElement('style');
    s.id = 'gjs-mapbox-logo-hide';
    s.textContent = '.mapboxgl-ctrl-logo { display: none !important; }';
    document.head.appendChild(s);
  }
  
  // Map of component types to lazy-loaded React components
  const componentMap = {
    'MapboxWithPins': LazyMapboxWithPins,
    'MapboxSearchWithFilters': LazyMapboxSearchWithFilters,
    'MapboxWithDraw': LazyMapboxWithDraw
  };
  
  // Function to render React component with Suspense
  function renderReactComponent(wrapper, componentType, props = {}) {
    try {
      const ReactComponent = componentMap[componentType];
      if (!ReactComponent) {
        console.warn(`Unknown component type: ${componentType}`);
        return;
      }

      const root = createRoot(wrapper);
      
      // Create loading fallback element
      const LoadingFallback = React.createElement('div', {
        style: { padding: '20px', textAlign: 'center' }
      }, 'Loading map...');
      
      // Render with Suspense for lazy loading
      root.render(
        React.createElement(
          React.Suspense,
          { fallback: LoadingFallback },
          React.createElement(ReactComponent, props)
        )
      );
      
      // Store root for cleanup
      wrapper._reactRoot = root;
      
      console.log(`✅ Rendered ${componentType} component`);
    } catch (error) {
      console.error(`❌ Error rendering ${componentType}:`, error);
    }
  }

  // Function to cleanup React component
  function cleanupReactComponent(wrapper) {
    try {
      if (wrapper._reactRoot) {
        wrapper._reactRoot.unmount();
        wrapper._reactRoot = null;
        console.log('🧹 Cleaned up React component');
      }
    } catch (error) {
      console.error('❌ Error cleaning up React component:', error);
    }
  }

  // Listen for component additions
  editor.on('component:add', (component) => {
    try {
      const componentType = component.get('type');
      if (componentMap[componentType]) {
        console.log(`🎯 Adding Mapbox component: ${componentType}`);
        
        // Wait for component to be added to DOM
        setTimeout(() => {
          try {
            const el = component.getEl();
            if (el && el.querySelector) {
              const wrapper = el.querySelector('.mapbox-container') || el;
              if (wrapper) {
                renderReactComponent(wrapper, componentType);
              } else {
                console.warn('Wrapper element not found for', componentType);
              }
            }
          } catch (error) {
            console.error('Error in component:add handler:', error);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error in component:add event:', error);
    }
  });

  // Listen for component updates
  editor.on('component:update', (component) => {
    try {
      const componentType = component.get('type');
      if (componentMap[componentType]) {
        console.log(`🔄 Updating Mapbox component: ${componentType}`);
        
        setTimeout(() => {
          try {
            const el = component.getEl();
            if (el && el.querySelector) {
              const wrapper = el.querySelector('.mapbox-container') || el;
              if (wrapper) {
                // Cleanup existing component
                cleanupReactComponent(wrapper);
                // Render new component
                renderReactComponent(wrapper, componentType);
              }
            }
          } catch (error) {
            console.error('Error in component:update handler:', error);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error in component:update event:', error);
    }
  });

  // Listen for component removals
  editor.on('component:remove', (component) => {
    try {
      const componentType = component.get('type');
      if (componentMap[componentType]) {
        console.log(`🗑️ Removing Mapbox component: ${componentType}`);
        
        const el = component.getEl();
        if (el && el.querySelector) {
          const wrapper = el.querySelector('.mapbox-container') || el;
          if (wrapper) {
            cleanupReactComponent(wrapper);
          }
        }
      }
    } catch (error) {
      console.error('Error in component:remove event:', error);
    }
  });

  // Add command to manually render all Mapbox components
  editor.Commands.add('render-mapbox-components', {
    run: (editor) => {
      console.log('🎨 Manually rendering all Mapbox components...');
      
      const components = editor.DomComponents.getComponents();
      components.forEach(component => {
        try {
          const componentType = component.get('type');
          if (componentMap[componentType]) {
            const el = component.getEl();
            if (el && el.querySelector) {
              const wrapper = el.querySelector('.mapbox-container') || el;
              if (wrapper) {
                cleanupReactComponent(wrapper);
                renderReactComponent(wrapper, componentType);
              }
            }
          }
        } catch (error) {
          console.error('Error rendering component:', error);
        }
      });
      
      console.log('✅ Finished rendering all Mapbox components');
    }
  });

  // Add toolbar button to manually trigger rendering
  editor.Panels.addButton('options', {
    id: 'render-mapbox',
    className: 'btn-render-mapbox',
    command: 'render-mapbox-components',
    attributes: { title: 'Render Mapbox Components' },
    label: '🗺️ Render Maps'
  });

  console.log('✅ Mapbox Vanilla GL renderer registered successfully');
} 